import { FormulaEditor, useEditorParseData } from 'components/FormulaEditor';
import { ModalDialog } from 'components/ModalDialog';
import { ReadField } from 'components/ReadField';
import { RequestStatus } from 'features/request/requestSlice';
import type { ProjectMethodologyEnum } from 'gql/graphql';
import { CatalogOtherCostVariableTypeEnum } from 'gql/graphql';
import { getOtherFormulaSourceObjectGql } from 'pages/Project/Common/Contract/contractQueries';
import { GridItem, Badge } from 'uikit';

import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { useGetTypesData } from '../CatalogOthersType/hooks';

import { useOtherVariable } from './hooks';
import { getVariableTypeChoiceLabel } from './utils';

import Styles from './createOrEditVariable.module.css';

export type IViewOtherVariableProps = {
    id: number;
    onClose: () => void;
    methodology: ProjectMethodologyEnum;
    isGlobal?: boolean;
};

export const ViewOtherVariable = ({ id, onClose, methodology, isGlobal = false }: IViewOtherVariableProps) => {
    const { variableData, variableStatus } = useOtherVariable(id, methodology, false);

    const { control, setValue } = useForm({});

    const { typesData } = useGetTypesData({
        methodology,
        withEffect: Boolean(variableData && variableData?.type === CatalogOtherCostVariableTypeEnum?.OtherCostType),
    });

    const currentTypeData = useMemo(
        () => typesData.find((t) => t.id === variableData?.otherCostTypeId),
        [typesData, variableData?.otherCostTypeId],
    );

    const { deserializeFromBackend } = useEditorParseData({});

    const formulaQueryVariables = useMemo(
        () => ({
            skip: 0,
            take: 1000,
            methodology,
            globalOnly: isGlobal,
        }),
        [isGlobal, methodology],
    );

    useEffect(() => {
        if (variableData?.formula) {
            setValue('formula', deserializeFromBackend(variableData.formula));
        }
    }, [deserializeFromBackend, setValue, variableData]);

    return (
        <ModalDialog
            isOpen
            isLoading={!!id && variableStatus === RequestStatus.FETCHING}
            title="Просмотр переменной"
            btnCancelLabel="Закрыть"
            onClose={onClose}
            bodyWidth={600}
            withTitleCloseButton
            isOnClickOutsideDisable
        >
            <div className={Styles.modalBody}>
                <GridItem>
                    <ReadField value={variableData?.name} label="Наименование переменной" />
                </GridItem>
                <GridItem>
                    <ReadField value={variableData?.group?.name} label="Группа переменных" />
                </GridItem>
                <GridItem>
                    <ReadField
                        value={getVariableTypeChoiceLabel({
                            label: variableData?.type ?? '',
                        })}
                        label="Тип переменной"
                    />
                </GridItem>
                {variableData?.type && variableData?.type === CatalogOtherCostVariableTypeEnum.Simple && (
                    <>
                        <ReadField
                            value={variableData?.entities?.map((e) => (
                                <Badge
                                    className={Styles.entities}
                                    key={e.uniqueId}
                                    size="xs"
                                    status="system"
                                    label={e.name}
                                />
                            ))}
                            label="Сущность для переменной"
                        />
                        <ReadField value={variableData?.dataSource?.name} label="Источник данных" />
                    </>
                )}
                {variableData?.type && variableData?.type === CatalogOtherCostVariableTypeEnum.Composite && (
                    <FormulaEditor
                        name="formula"
                        control={control}
                        getObjectSourceQuery={getOtherFormulaSourceObjectGql}
                        queryVariables={formulaQueryVariables}
                        tooltipTitle="Формула расчета"
                        tooltipMessage=""
                        tooltipTitleClassName={Styles.formulaTitle}
                        wrapperClassName={Styles.formulaWrapper}
                        methodology={methodology}
                        isReadOnly
                    />
                )}
                {variableData?.type && variableData?.type === CatalogOtherCostVariableTypeEnum.OtherCostType && (
                    <ReadField value={currentTypeData?.name ?? ''} label="Тип прочих" />
                )}
            </div>
        </ModalDialog>
    );
};

ViewOtherVariable.displayName = 'ViewOtherVariable';
